import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoCard from "@/components/feature/VideoCard";
import SectionHeader from "@/components/feature/SectionHeader";
import EmptyState from "@/components/feature/EmptyState";
import PlayerModal from "@/pages/detail/components/PlayerModal";
import { categoryMeta } from "@/mocks/categories";
import { getRelated, getVideoById } from "@/utils/video";
import { useFavorites } from "@/hooks/useFavorites";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const item = id ? getVideoById(id) : undefined;

  const [playerOpen, setPlayerOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const { isFavorite, toggle } = useFavorites();

  useEffect(() => {
    document.title = item
      ? `${item.title}（${item.year}）- 详情 - 91看片`
      : "影视不存在 - 91看片";
    window.scrollTo({ top: 0 });
  }, [item]);

  if (!item) {
    return (
      <SiteLayout>
        <div className="mx-auto max-w-[1400px] px-4 py-20 md:px-6">
          <EmptyState
            icon="ri-film-line"
            title="影片不存在或加载失败"
            description="你访问的影视详情可能已被移除，或者链接有误。返回首页继续浏览其他精彩内容。"
          >
            <Link
              to="/"
              className="cursor-pointer rounded-md bg-accent-500 px-4 py-2 text-sm font-semibold text-background-50"
            >
              返回首页
            </Link>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-semibold text-foreground-700"
            >
              返回上一页
            </button>
          </EmptyState>
        </div>
      </SiteLayout>
    );
  }

  const meta = categoryMeta[item.category];
  const related = getRelated(item, 8);
  const favorite = isFavorite(item.id);

  const infoRows: { label: string; value: string }[] = [
    { label: "导演", value: item.director },
    { label: "主演", value: item.cast.join(" / ") },
    { label: "类型", value: item.genres.join(" / ") },
    { label: "地区", value: item.region },
    { label: "年份", value: `${item.year}` },
  ];
  if (item.episodes) infoRows.push({ label: "集数", value: `全 ${item.episodes} 集` });
  if (item.updateStatus) infoRows.push({ label: "更新状态", value: item.updateStatus });
  if (item.platform) infoRows.push({ label: "播出平台", value: item.platform });
  if (item.playStatus) infoRows.push({ label: "播放状态", value: item.playStatus });

  const handleFavorite = () => {
    toggle(item.id);
    setFeedback(favorite ? "已取消收藏" : "已加入收藏");
    window.setTimeout(() => setFeedback(""), 2000);
  };

  return (
    <SiteLayout>
      <div className="mx-auto max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: meta.name, to: `/category/${item.category}` },
            { label: item.title },
          ]}
        />

        <div className="mt-5 flex flex-col gap-6 lg:flex-row lg:gap-8">
          <div className="w-full shrink-0 sm:w-[240px] md:w-[280px]">
            <div
              className="relative w-full overflow-hidden rounded-lg bg-background-200"
              style={{ aspectRatio: "3 / 4" }}
            >
              <img
                src={item.cover}
                alt={`${item.title}封面海报`}
                className="h-full w-full object-top object-cover"
              />
              <span className="absolute left-2 top-2 rounded-md bg-primary-950/80 px-2 py-0.5 text-xs font-bold text-accent-300">
                {item.rating.toFixed(1)} 分
              </span>
            </div>
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-primary-100 px-2 py-1 text-xs font-semibold text-primary-700">
                {meta.name}
              </span>
              {item.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-md bg-secondary-100 px-2 py-1 text-xs font-medium text-secondary-800"
                >
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="mt-3 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-4xl">
              {item.title}
            </h1>
            <p className="mt-2 text-sm text-foreground-500">
              {item.year} · {item.region} · {item.genres.join(" / ")}
            </p>

            <div className="mt-5 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setPlayerOpen(true)}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
              >
                <i className="ri-play-fill text-lg" />
                立即播放
              </button>
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border px-5 py-3 text-sm font-semibold transition-colors ${
                  favorite
                    ? "border-accent-500 bg-accent-100 text-accent-800"
                    : "border-background-300 bg-background-50 text-foreground-700 hover:border-accent-400 hover:text-accent-600"
                }`}
              >
                <i className={`${favorite ? "ri-heart-3-fill" : "ri-heart-3-line"} text-base`} />
                {favorite ? "已收藏" : "收藏"}
              </button>
              {feedback && (
                <span className="flex items-center gap-1 self-center text-xs font-medium text-accent-600">
                  <i className="ri-check-line" />
                  {feedback}
                </span>
              )}
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">剧情简介</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">{item.synopsis}</p>
            </div>

            <div className="mt-4 rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">影视信息</h2>
              <dl className="mt-3 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex gap-3 text-sm">
                    <dt className="w-16 shrink-0 text-foreground-400">{row.label}</dt>
                    <dd className="min-w-0 flex-1 text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-background-200 pt-4">
                <span className="text-sm text-foreground-400">所属分类</span>
                <Link
                  to={`/category/${item.category}`}
                  className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-primary-600 px-3 py-1.5 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-700"
                >
                  <i className="ri-price-tag-3-line" />
                  {meta.name}频道
                </Link>
              </div>
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <section className="mt-12 md:mt-14">
            <SectionHeader
              title="相关推荐"
              subtitle="根据类型与标签为你匹配的相似影视作品"
              actionLabel={`更多${meta.name}`}
              actionTo={`/category/${item.category}`}
            />
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {related.map((rel) => (
                <VideoCard key={rel.id} item={rel} />
              ))}
            </div>
          </section>
        )}
      </div>

      {playerOpen && <PlayerModal item={item} onClose={() => setPlayerOpen(false)} />}
    </SiteLayout>
  );
}